import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import { useQuery } from "react-query";
import {
  getMonthWiseStats,
  getDayWiseStats,
} from "../../services/analyticsService";
import { formatAmount } from "../../utils/formatters";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from "react-router-dom";

const toLocalMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getInitialStartDate = (timeRange, endDate = new Date()) => {
  if (timeRange === "month") {
    return toLocalMidnight(
      new Date(endDate.getFullYear(), endDate.getMonth() - 2, 1) // 3 months (current month + 2 previous months)
    );
  } else {
    const d = new Date(endDate);
    d.setDate(d.getDate() - 90); // 3 months (90 days)
    return toLocalMidnight(d);
  }
};

const ZoneWiseSalesTrend = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const STORAGE_KEY = "filters:zone-wise";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) { }
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }

  // Initialize timeRange first (no hourly - only month or day)
  const rawInitialTimeRange = location.state?.timeRange || savedFilters?.timeRange || "month";
  const initialTimeRange = rawInitialTimeRange === "hour" ? "day" : rawInitialTimeRange;

  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [endDate, setEndDate] = useState(() => {
    if (location.state?.endDate) {
      return toLocalMidnight(new Date(location.state.endDate));
    }
    if (savedFilters?.endDate) {
      return toLocalMidnight(new Date(savedFilters.endDate));
    }
    return toLocalMidnight(new Date());
  });
  const [startDate, setStartDate] = useState(() => {
    if (location.state?.startDate) {
      return toLocalMidnight(new Date(location.state.startDate));
    }
    if (savedFilters?.startDate) {
      return toLocalMidnight(new Date(savedFilters.startDate));
    }
    return toLocalMidnight(getInitialStartDate(initialTimeRange, new Date()));
  });
  const [showQuantity, setShowQuantity] = useState(
    location.state?.showQuantity ?? savedFilters?.showQuantity ?? false
  );
  const [postingAccount, setPostingAccount] = useState(
    location.state?.postingAccount || savedFilters?.postingAccount || ""
  );
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          startDate: startDate?.toISOString?.() || startDate,
          endDate: endDate?.toISOString?.() || endDate,
          timeRange,
          showQuantity,
          postingAccount,
        })
      );
    } catch (_) { }
  }, [startDate, endDate, timeRange, showQuantity, postingAccount]);

  // Track previous timeRange to detect when switching
  const prevTimeRangeRef = useRef(timeRange);
  
  // Adjust dates when switching time ranges
  useEffect(() => {
    // Only adjust when switching TO a different time range
    if (prevTimeRangeRef.current === timeRange) {
      prevTimeRangeRef.current = timeRange;
      if (toLocalMidnight(endDate) < startDate) {
        const newStartDate = getInitialStartDate(timeRange, endDate);
        setStartDate(toLocalMidnight(newStartDate));
      }
      return;
    }

    if (timeRange === "month" && endDate) {
      const endDateMonth = endDate.getMonth();
      const endDateYear = endDate.getFullYear();
      
      // Always set start date to 1st of the month when in monthly view
      const firstDayOfMonth = new Date(endDateYear, endDateMonth, 1);
      setStartDate(toLocalMidnight(firstDayOfMonth));
      
      // Set end date to last day of the month
      const lastDayOfMonth = new Date(endDateYear, endDateMonth + 1, 0);
      setEndDate(toLocalMidnight(lastDayOfMonth));
    }
    
    // Update the ref for next comparison
    prevTimeRangeRef.current = timeRange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, endDate]);

  const handleStartDateChange = (date) => {
    if (timeRange === "month") {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      setStartDate(toLocalMidnight(firstDay));
    } else {
      setStartDate(toLocalMidnight(date));
    }
  };

  const handleEndDateChange = (date) => {
    if (timeRange === "month") {
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setEndDate(toLocalMidnight(lastDay));
      const newStartDate = new Date(date.getFullYear(), date.getMonth() - 2, 1);
      if (toLocalMidnight(date) < startDate) {
        setStartDate(toLocalMidnight(newStartDate));
      }
    } else {
      setEndDate(toLocalMidnight(date));
      if (toLocalMidnight(date) < startDate) {
        setStartDate(getInitialStartDate(timeRange, date));
      }
    }
  };

  const { data: salesData, isLoading: isSalesLoading } = useQuery(
    ["zoneSalesData", timeRange, startDate, endDate, postingAccount],
    () => {
      // Format dates - backend stats endpoints expect YYYY-MM-DD format for all time ranges
      // Helper function to format date as YYYY-MM-DD
      const formatDateToYYYYMMDD = (date) => {
        if (!date) return null;
        let dateObj = date;
        if (!(date instanceof Date)) {
          dateObj = new Date(date);
        }
        if (isNaN(dateObj.getTime())) return null;
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);

      if (!formattedStartDate || !formattedEndDate) {
        throw new Error("Invalid date format");
      }

      const params = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        statType: "zone",
        groupBy: "day",
        ...(postingAccount && { postingAccount }),
      };
      return timeRange === "month"
        ? getMonthWiseStats(params)
        : getDayWiseStats(params);
    },
    {
      onError: (error) => {
        //console.log("Zone Sales Data Error:", error);
        toast.error(error.message || "Failed to load zone sales data");
      },
    }
  );


  const formatCell = (data) => {
    if (!data) return showQuantity ? "0" : "₹0";
    return showQuantity
      ? `${data.qty}`
      : `${formatAmount(data.net_amount ?? 0)}`;
  };

  // Export to CSV function (aligned with PortalWiseSalesTrend: loading state + try/finally)
  const handleExportToCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        startMonth: startDate.toLocaleDateString("en-CA").split("T")[0],
        endMonth: endDate.toLocaleDateString("en-CA").split("T")[0],
        qtyReq: showQuantity,
        statType: "zone",
        exportToCsv: "true",
        ...(postingAccount && { postingAccount }),
      });

      const baseURL =
        process.env.REACT_APP_API_BASE_URL ||
        "https://digitalapi.palsonsderma.com";
      const response = await fetch(
        `${baseURL}/data/order/${timeRange === "month" ? "month-wise-stats" : "day-wise-stats"}?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      const contentDisposition = response.headers.get("Content-Disposition");
      const match = contentDisposition?.match(/filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i);
      const filename =
        match?.[1]?.trim().replace(/^["']|["']$/g, "")
          ? decodeURIComponent(match[1].trim().replace(/^["']|["']$/g, ""))
          : `zone-wise-sales-${startDate.toLocaleDateString("en-CA").split("T")[0]}-to-${endDate.toLocaleDateString("en-CA").split("T")[0]}${postingAccount ? `-${postingAccount}` : ""}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download export");
    } finally {
      setIsExporting(false);
    }
  };

  // Prepare data for charts
  const prepareChartData = () => {
    //console.log("ZoneWiseSalesTrend - salesData:", salesData);
    if (!salesData) return { barData: [], pieData: [] };

    // Filter out totals and get only zone data
    const zoneData = salesData.filter((sale) => {
      const name = sale.zone || sale.stat_value;
      //console.log("Checking zone:", name, "sale:", sale);
      // Filter out "total" and "grand total" (case insensitive)
      const lowerName = name.toLowerCase();
      return name && lowerName !== "total" && lowerName !== "grand total";
    });

    //console.log("Filtered zoneData:", zoneData);

    // Calculate totals for both amount and quantity
    const totalAmount = zoneData.reduce((sum, sale) => {
      let amount = 0;
      if (timeRange === "month") {
        amount = sale.total?.net_amount || 0;
      } else {
        amount = sale.total_net_amount || 0;
      }
      return sum + amount;
    }, 0);

    const totalQuantity = zoneData.reduce((sum, sale) => {
      let qty = 0;
      if (timeRange === "month") {
        qty = sale.total?.qty || 0;
      } else {
        qty = sale.total_qty || 0;
      }
      return sum + qty;
    }, 0);

    // Bar chart data (always amount)
    const barData = zoneData.map((sale) => {
      let amount = 0;
      if (timeRange === "month") {
        amount = sale.total?.net_amount || 0;
      } else {
        amount = sale.total_net_amount || 0;
      }

      const percentage =
        totalAmount > 0 ? ((amount / totalAmount) * 100).toFixed(1) : 0;

      return {
        name: sale.zone || sale.stat_value,
        value: amount,
        percentage: parseFloat(percentage),
        formattedValue: formatAmount(amount),
      };
    });

    // Pie chart data (always quantity)
    const pieData = zoneData.map((sale) => {
      let qty = 0;
      if (timeRange === "month") {
        qty = sale.total?.qty || 0;
      } else {
        qty = sale.total_qty || 0;
      }

      const percentage =
        totalQuantity > 0 ? ((qty / totalQuantity) * 100).toFixed(1) : 0;

      return {
        name: sale.zone || sale.stat_value,
        value: qty,
        percentage: parseFloat(percentage),
        formattedValue: qty.toString(),
      };
    });

    return {
      barData,
      pieData,
    };
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  const RADIAN = Math.PI / 180;
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, value }) => {
    if (value == null) return null;
    const label = `${value} Sales`;
    const radius = outerRadius * 0.7;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const labelWidth = label.length * 7 + 8;
    const labelHeight = 14;
    const rectX = x - labelWidth / 2;
    const rectY = y - labelHeight / 2;
    return (
      <g>
        <rect
          x={rectX}
          y={rectY}
          width={labelWidth}
          height={labelHeight}
          rx={3}
          ry={3}
          fill="rgba(255,255,255,0.9)"
          stroke="#e5e7eb"
        />
        <text
          x={x}
          y={rectY + labelHeight - 4}
          fill="#374151"
          fontSize={10}
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
    );
  };

  // Label renderer for vertical bars (pill background above bar)
  const renderBarLabel = ({ x, y, width, height, value }) => {
    if (value == null) return null;
    const label = formatAmount(value);
    // const labelWidth = label.length * 7 + 8;
    // const labelHeight = 14;
    // const centerX = x + width / 2;
    // const rectX = centerX - labelWidth / 2;
    // const rectY = y - labelHeight - 6;
    // return (
    //   <g>
    //     <rect
    //       x={rectX}
    //       y={rectY}
    //       width={labelWidth}
    //       height={labelHeight}
    //       rx={3}
    //       ry={3}
    //       fill="rgba(255,255,255,0.9)"
    //       stroke="#e5e7eb"
    //     />
    //     <text
    //       x={centerX}
    //       y={rectY + labelHeight - 4}
    //       fill="#374151"
    //       fontSize={10}
    //       textAnchor="middle"
    //     >
    //       {label}
    //     </text>
    //   </g>
    // );

    const isBarTooSmall = height < 30;

    return (
      <text
        x={isBarTooSmall ? x + width + 26 : x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline={isBarTooSmall ? "middle" : "middle"}
        fill={isBarTooSmall ? "black" : "white"}
        fontSize="12"
        fontWeight="bold"
        transform={`rotate(-90, ${x + width / 2}, ${y + height / 2})`}
      >
        {label}
      </text>
    );

  };

  const renderBarChart = () => {
    const { barData } = prepareChartData();

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          Zone Wise Sales Revenue
        </h3>
        <div className="relative overflow-visible">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={barData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6b7280" }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 14, fill: "#6b7280" }}
                tickFormatter={(value) => formatAmount(value)}
              />
              <Tooltip
                formatter={(value, name) => [
                  formatAmount(value),
                  "Amount",
                ]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  maxWidth: 280,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                wrapperStyle={{ maxWidth: 280 }}
                labelStyle={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
              />
              <Legend />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                {barData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`hsl(${200 + index * 30}, 70%, ${50 + index * 5}%)`}
                  />
                ))}
                <LabelList content={renderBarLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderPieChart = () => {
    const { pieData } = prepareChartData();

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          Zone Wise Sales Distribution
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderPieLabel}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                `${value} Sales`,
                props.payload.name || "Zone",
              ]}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="inline-block ml-1 text-gray-400">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
            <path d="M6 10L3 6H9L6 10Z" opacity="0.4" />
          </svg>
        </span>
      );
    }

    if (sortDirection === "asc") {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
          </svg>
        </span>
      );
    } else {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 10L3 6H9L6 10Z" />
          </svg>
        </span>
      );
    }
  };

  const sortData = (data, field, direction) => {
    if (!field) return data;

    // Separate grand total rows from regular data
    const grandTotalRows = data.filter((item) => {
      const name = (item.zone || item.stat_value || "").toLowerCase();
      return name.includes("total") || name.includes("grand");
    });

    const regularData = data.filter((item) => {
      const name = (item.zone || item.stat_value || "").toLowerCase();
      return !(name.includes("total") || name.includes("grand"));
    });

    // Sort only the regular data
    const sortedRegularData = [...regularData].sort((a, b) => {
      let aVal, bVal;

      if (field === "zone") {
        // For zone name column
        aVal = (a.zone || a.stat_value || "").toLowerCase();
        bVal = (b.zone || b.stat_value || "").toLowerCase();
      } else if (field === "total") {
        if (timeRange === "month") {
          aVal = showQuantity ? a.total?.qty || 0 : a.total?.net_amount || 0;
          bVal = showQuantity ? b.total?.qty || 0 : b.total?.net_amount || 0;
        } else {
          aVal = showQuantity ? a.total_qty || 0 : a.total_net_amount || 0;
          bVal = showQuantity ? b.total_qty || 0 : b.total_net_amount || 0;
        }
      } else {
        // For month/date columns
        if (timeRange === "month") {
          aVal = showQuantity ? a[field]?.qty || 0 : a[field]?.net_amount || 0;
          bVal = showQuantity ? b[field]?.qty || 0 : b[field]?.net_amount || 0;
        } else {
          const aTimeData = a.daily_data;
          const bTimeData = b.daily_data;
          aVal = showQuantity
            ? aTimeData?.[field]?.qty || 0
            : aTimeData?.[field]?.net_amount || 0;
          bVal = showQuantity
            ? bTimeData?.[field]?.qty || 0
            : bTimeData?.[field]?.net_amount || 0;
        }
      }

      if (direction === "asc") {
        if (typeof aVal === "string") return aVal.localeCompare(bVal);
        return aVal - bVal;
      } else {
        if (typeof aVal === "string") return bVal.localeCompare(aVal);
        return bVal - aVal;
      }
    });

    // Combine sorted regular data with grand total rows at the end
    return [...sortedRegularData, ...grandTotalRows];
  };

  const renderTable = () => {
    if (!salesData) return null;
    const sales = sortData(salesData, sortField, sortDirection);
    if (timeRange === "month") {
      // Generate monthList between startDate and endDate
      const monthList = [];
      let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (current <= end) {
        monthList.push({
          key: `${current.getFullYear()}-${current.getMonth() + 1}`,
          label: current.toLocaleString("default", { month: "short" }),
          year: current.getFullYear(),
          month: current.getMonth(),
        });
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("zone")}
                >
                  <div className="flex items-center">
                    Zone
                    {getSortIcon("zone")}
                  </div>
                </th>
                {monthList.map((m) => (
                  <th
                    key={m.key}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(m.label)}
                  >
                    <div className="flex items-center">
                      {m.label} {m.year}
                      {getSortIcon(m.label)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => {
                const isGrandTotal =
                  sale.zone?.toLowerCase().includes("total") ||
                  sale.zone?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={
                      isGrandTotal
                        ? "font-bold"
                        : "cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                    }
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                    onClick={() => {
                      if (!isGrandTotal && sale.zone) {
                        navigate("/analytics/zone-detail-sales-trend", {
                          state: {
                            fromPage: "/analytics/zone-wise-sales-trend",
                            zone: sale.zone,
                            startDate: startDate.toLocaleDateString("en-CA"),
                            endDate: endDate.toLocaleDateString("en-CA"),
                            timeRange: timeRange,
                            postingAccount: postingAccount,
                            showQuantity: showQuantity,
                          },
                        });
                      }
                    }}
                  >
                    <td className="whitespace-nowrap">{sale.zone}</td>
                    {monthList.map((m) => {
                      const key = m.label;
                      return <td key={m.key}>{formatCell(sale[key])}</td>;
                    })}
                    <td>{formatCell(sale.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // For daily view
      const dates = Object.keys(sales[0]?.daily_data || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => {
          const d = new Date(date);
          return {
            fullDate: date,
            displayDate: `${d.getDate()}-${d.toLocaleString("default", {
              month: "short",
            })}`,
          };
        });
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("zone")}
                >
                  <div className="flex items-center">
                    Zone
                    {getSortIcon("zone")}
                  </div>
                </th>
                {dates.map(({ fullDate, displayDate }) => (
                  <th
                    key={fullDate}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(fullDate)}
                  >
                    <div className="flex items-center">
                      {displayDate}
                      {getSortIcon(fullDate)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => {
                const isGrandTotal =
                  sale.stat_value?.toLowerCase().includes("total") ||
                  sale.stat_value?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={
                      isGrandTotal
                        ? "font-bold"
                        : "cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                    }
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                    onClick={() => {
                      if (!isGrandTotal && sale.stat_value) {
                        navigate("/analytics/zone-detail-sales-trend", {
                          state: {
                            fromPage: "/analytics/zone-wise-sales-trend",
                            zone: sale.stat_value,
                            startDate: startDate.toLocaleDateString("en-CA"),
                            endDate: endDate.toLocaleDateString("en-CA"),
                            timeRange: timeRange,
                            postingAccount: postingAccount,
                            showQuantity: showQuantity,
                          },
                        });
                      }
                    }}
                  >
                    <td className="whitespace-nowrap">{sale.stat_value}</td>
                    {dates.map(({ fullDate }) => (
                      <td key={`${sale.stat_value}-${fullDate}`}>
                        {formatCell(sale.daily_data?.[fullDate])}
                      </td>
                    ))}
                    <td>{formatAmount(sale.total_net_amount ?? 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  if (isSalesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <h1 className="text-2xl font-bold">Zone Wise Sales Trend</h1>
        <div className="flex flex-wrap gap-4 items-center w-full lg:w-auto">
        <select
            className="select select-bordered w-full lg:w-40"
            value={postingAccount}
            onChange={(e) => setPostingAccount(e.target.value)}
          >
            <option value="">All Platforms</option>
            <option value="amazon">Amazon</option>
            <option value="flipkart">Flipkart</option>
            <option value="shopify">Shopify</option>
            <option value="tata 1mg">Tata 1mg</option>
            <option value="palsons derma pvt ltd">Palsons Derma Pvt Ltd</option>
          </select>
          <div className="flex gap-2 items-center w-full lg:w-auto">
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="text-sm input input-bordered w-full lg:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              minDate={
                timeRange === "month"
                  ? new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1)
                  : new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000)
              }
              maxDate={endDate}
            />
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="text-sm input input-bordered w-full lg:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              maxDate={new Date()}
            />
          </div>

          <div className="btn-group">
            <button
              className={`btn btn-sm ${timeRange === "day" ? "btn-primary" : "btn-ghost"
                }`}
              onClick={() => setTimeRange("day")}
            >
              Daily
            </button>
            <button
              className={`btn btn-sm ${timeRange === "month" ? "btn-primary" : "btn-ghost"
                }`}
              onClick={() => setTimeRange("month")}
            >
              Monthly
            </button>
          </div>


          

          <button
            className="btn btn-primary btn-sm"
            onClick={handleExportToCSV}
            disabled={isSalesLoading || isExporting}
          >
            {isSalesLoading || isExporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              "Export"
            )}
          </button>
        </div>
      </div>

      {/* Charts Grid - Side by side on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderBarChart()}
        {renderPieChart()}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              Zone Wise Sales Revenue
            </h3>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={showQuantity}
                  onChange={(e) => setShowQuantity(e.target.checked)}
                />
                <span className="text-sm font-medium">Show Sales Count</span>
              </label>
            </div>
          </div>
          <div className="overflow-x-auto">{renderTable()}</div>
        </div>
      </div>
    </div>
  );
};

export default ZoneWiseSalesTrend;
